import { useMemo, useRef } from 'react';

import { Box, type BoxProps } from '@frontend/legacy-components';

import { AppPanelContainerProvider } from './context';
import { useResizablePanel } from './hooks';
import {
  DEFAULT_MIN_WIDTH,
  DEFAULT_MAX_WIDTH,
  getPanelWidth,
  type PanelSize,
} from './utils';

interface AppPanelContainerProps extends React.PropsWithChildren<BoxProps> {
  size: PanelSize;
  isCollapsed: boolean;
  isVisible?: boolean;
  isResizable?: boolean;
  minWidth?: number;
  maxWidth?: number;
  side?: 'left' | 'right';
}

export const AppPanelContainer = ({
  isCollapsed,
  isVisible = true,
  size,
  children,
  isResizable = false,
  minWidth = DEFAULT_MIN_WIDTH,
  maxWidth = DEFAULT_MAX_WIDTH,
  side = 'right',
  ...boxProps
}: AppPanelContainerProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const defaultSize = getPanelWidth(size);

  const {
    width,
    isResizing,
    isResizableEnabled,
    isTransitioning,
    resizeHandleOnMouseDown,
    resizeHandleOnDoubleClick,
  } = useResizablePanel({
    isResizable,
    defaultSize,
    minSize: minWidth,
    maxSize: maxWidth,
    side,
  });

  const { finalWidth, marginRight } = useMemo(() => {
    // During transition or when resizable is not enabled, use defaultSize
    // This ensures smooth transitions when switching panels
    const defaultWidthString = `${defaultSize}px`;
    const panelWidth =
      isResizable && isResizableEnabled && !isTransitioning
        ? width
        : defaultWidthString;
    return {
      finalWidth: panelWidth,
      marginRight: isCollapsed ? `calc(-1 * ${panelWidth})` : '0',
    };
  }, [
    isCollapsed,
    isResizable,
    isResizableEnabled,
    isTransitioning,
    width,
    defaultSize,
  ]);

  const resizableProps =
    isResizable && isResizableEnabled
      ? {
          ref: panelRef,
          minWidth: `${minWidth}px`,
          maxWidth: `${maxWidth}px`,
          overflowX: 'visible' as const,
          overflowY: isVisible ? ('auto' as const) : ('hidden' as const),
          style: {
            transition: isResizing ? 'none' : 'all 0.3s ease',
          },
        }
      : {
          overflow: isVisible ? 'auto' : 'hidden',
          transition: 'all 0.3s ease' as const,
        };

  return (
    <AppPanelContainerProvider
      value={{
        onResizeMouseDown: resizeHandleOnMouseDown,
        onResizeDoubleClick: resizeHandleOnDoubleClick,
        isResizing,
        isResizable: isResizableEnabled,
        isVisible,
        isCollapsed,
      }}
    >
      <Box
        position="relative"
        width={finalWidth}
        maxWidth={finalWidth}
        marginRight={marginRight}
        opacity={isVisible ? 1 : 0}
        visibility={isVisible ? 'visible' : 'hidden'}
        pointerEvents={isVisible ? 'auto' : 'none'}
        isolation="isolate"
        {...resizableProps}
        {...boxProps}
      >
        {children}
      </Box>
    </AppPanelContainerProvider>
  );
};
